import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, LayersIcon, SquareIcon, FootprintsIcon, MountainIcon, GridIcon, TreesIcon, ChevronDownIcon } from 'lucide-react';
import { FCIQuoteForm } from './FCIQuoteForm';
interface FCILandingPageProps {
  initialService?: string;
  onClose: () => void;
}
export function FCILandingPage({
  initialService,
  onClose
}: FCILandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState<string>(initialService || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialService) {
      setSelectedService(initialService);
    }
  }, [initialService]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedService) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <FCIQuoteForm initialZip={zipCode} serviceSlug={selectedService} onClose={onClose} data-id="element-1355" />;
  }
  const services = [{
    id: 'carpet',
    name: 'Carpet',
    icon: <LayersIcon className="w-6 h-6" data-id="element-1356" />
  }, {
    id: 'laminate',
    name: 'Laminate',
    icon: <SquareIcon className="w-6 h-6" data-id="element-1357" />
  }, {
    id: 'vinyl',
    name: 'Vinyl',
    icon: <FootprintsIcon className="w-6 h-6" data-id="element-1358" />
  }, {
    id: 'stone',
    name: 'Stone',
    icon: <MountainIcon className="w-6 h-6" data-id="element-1359" />
  }, {
    id: 'tile',
    name: 'Tile',
    icon: <GridIcon className="w-6 h-6" data-id="element-1360" />
  }, {
    id: 'wood-floors',
    name: 'Wood Floors',
    icon: <TreesIcon className="w-6 h-6" data-id="element-1361" />
  }];
  const serviceLabel = services.find(s => s.id === selectedService)?.name || '';
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-1362">
      {/* Header */}
      <header className="bg-white py-4 px-6 md:px-12 flex justify-between items-center shadow-sm relative z-20" data-id="element-1363">
        <div className="flex flex-col" data-id="element-1364">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-[-4px]" data-id="element-1365">
            Partner
          </span>
          <div className="flex items-baseline gap-2" data-id="element-1366">
            <span className="text-2xl md:text-3xl font-black text-[#2D6A4F] tracking-tight" data-id="element-1367">
              FCI
            </span>
          </div>
          <span className="text-[10px] md:text-sm font-semibold text-gray-500" data-id="element-1368">
            Carpet &bull; Hardwood &bull; Laminate &bull; Tile &bull; Vinyl
            &bull; Stone
          </span>
        </div>
        <div className="hidden md:flex flex-col items-end" data-id="element-1369">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-[-4px]" data-id="element-1370">
            Call Us:
          </span>
          <span className="text-lg font-bold text-[#2D6A4F]" data-id="element-1371">
            855-FCI-FLOOR
          </span>
        </div>
      </header>

      {/* Offer Banner */}
      <section className="bg-[#2D6A4F] py-4 md:py-6 border-t border-[#1b4332]" data-id="element-1372">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1373">
          <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight" data-id="element-1374">
            The Flooring Store at Your Door
          </h2>
          <p className="text-green-100 text-sm mt-1" data-id="element-1375">
            Serving homeowners nationwide with excellence.
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-1376">
        <div className="absolute inset-0 z-0" data-id="element-1377">
          <img src="https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=1920&q=80" alt="Beautiful hardwood flooring" className="w-full h-full object-cover" data-id="element-1378" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-1379" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1380">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-1381">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1382">
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-1383">
                The Flooring Store at Your Door
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-1384">
                Get a free in-home estimate with our mobile showroom experience.
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-1385">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-1386">
                <h2 className="text-xl font-bold text-[#2D6A4F] text-center mb-6" data-id="element-1387">
                  {initialService ? 'Enter Your Zip Code To Start' : 'Select Flooring & Enter Your Zip'}
                </h2>

                {/* Service dropdown */}
                {!initialService && <div className="mb-6 relative" ref={dropdownRef} data-id="element-1388">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-green-50 ${selectedService ? 'border-[#2D6A4F]' : 'border-green-200'} ${dropdownOpen ? 'border-[#2D6A4F] shadow-md' : 'hover:border-green-300'}`} data-id="element-1389">
                      <div className="flex items-center gap-3" data-id="element-1390">
                        {selectedService ? <>
                            <span className="text-[#2D6A4F]" data-id="element-1391">
                              {services.find(s => s.id === selectedService)?.icon}
                            </span>
                            <span className="font-bold text-[#2D6A4F] text-lg" data-id="element-1392">
                              {services.find(s => s.id === selectedService)?.name}
                            </span>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-1393">
                            Choose flooring type...
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-[#2D6A4F] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-1394" />
                    </button>

                    <AnimatePresence data-id="element-1395">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-green-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-1396">
                          {services.map(svc => <button key={svc.id} onClick={() => {
                      setSelectedService(svc.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedService === svc.id ? 'bg-green-50 text-[#2D6A4F]' : 'bg-white text-gray-700 hover:bg-green-50 hover:text-[#2D6A4F]'} border-b border-green-100 last:border-b-0`} data-id="element-1397">
                              <span className={`${selectedService === svc.id ? 'text-[#2D6A4F]' : 'text-gray-400'}`} data-id="element-1398">
                                {svc.icon}
                              </span>
                              <span className="font-bold text-lg" data-id="element-1399">
                                {svc.name}
                              </span>
                              {selectedService === svc.id && <CheckCircle2Icon className="w-5 h-5 text-[#2D6A4F] ml-auto" data-id="element-1400" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {/* Show selected service badge when pre-selected */}
                {initialService && <div className="flex items-center justify-center gap-2 mb-6 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5" data-id="element-1401">
                    {services.find(s => s.id === initialService)?.icon}
                    <span className="font-bold text-[#2D6A4F]" data-id="element-1402">
                      {serviceLabel}
                    </span>
                  </div>}

                {/* Zip Code Input */}
                <div className="relative mb-6" data-id="element-1403">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter Zip Code" className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] transition-colors" maxLength={5} data-id="element-1404" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-1405">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-1406" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedService} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip && selectedService ? 'bg-[#FF6F00] hover:bg-orange-700 shadow-orange-500/30 hover:shadow-orange-500/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-1407">
                  Get Free Estimate
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-4" data-id="element-1408">
                  <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200" data-id="element-1409">
                    <ShieldCheckIcon className="w-5 h-5 text-green-600" data-id="element-1410" />
                    <span className="text-xs font-bold text-green-800 uppercase tracking-tight" data-id="element-1411">
                      Quality Guaranteed
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200" data-id="element-1412">
                    <div className="font-black text-slate-700 text-sm" data-id="element-1413">BBB</div>
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-tight" data-id="element-1414">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Green Accent Bar */}
      <div className="w-full h-3 bg-[#2D6A4F]" data-id="element-1415" />

      {/* What to Expect Section */}
      <section className="py-20 bg-white" data-id="element-1416">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1417">
          <div className="text-center mb-16" data-id="element-1418">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-1419">
              What to Expect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-1420">
            {[{
            title: 'Choose Your Flooring',
            desc: 'Select from carpet, hardwood, laminate, tile, vinyl, or stone. We have options to fit every style and budget.',
            img: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=400&h=400&fit=crop'
          }, {
            title: 'Free In-Home Consultation',
            desc: 'A Design Associate brings the mobile showroom to your home with thousands of samples, so you can see them in your own lighting.',
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Professional Installation',
            desc: 'Expert installers handle everything from moving furniture to the final cleanup, backed by a quality guarantee.',
            img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-1421">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-1422">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-1423" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1424">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1425">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#FAFAFA]" data-id="element-1426">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1427">
          <div className="text-center mb-12" data-id="element-1428">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-1429">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1430">
            {[{
            name: 'Sarah T.',
            location: 'Austin, TX',
            text: 'The mobile showroom was amazing! Being able to see the hardwood samples in my own living room lighting made the decision so much easier.'
          }, {
            name: 'David & Lisa M.',
            location: 'Denver, CO',
            text: 'The installation crew was fantastic. They moved all our heavy furniture, installed the new carpet perfectly, and cleaned up everything before leaving.'
          }, {
            name: 'Robert C.',
            location: 'Charlotte, NC',
            text: 'We had luxury vinyl plank installed throughout our first floor. The design associate was incredibly helpful and the final result looks stunning.'
          }].map((review, idx) => <motion.div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-1431">
                <div className="flex text-yellow-400 mb-4" data-id="element-1432">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-1433" />)}
                </div>
                <p className="text-gray-600 mb-6 italic" data-id="element-1434">
                  &ldquo;{review.text}&rdquo;
                </p>
                <div data-id="element-1435">
                  <p className="font-bold text-gray-900" data-id="element-1436">{review.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-1437">{review.location}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2D6A4F] py-16" data-id="element-1438">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1439">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-1440">
            <div className="flex flex-col gap-8" data-id="element-1441">
              <div className="flex flex-col" data-id="element-1442">
                <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider mb-[-4px]" data-id="element-1443">
                  Partner
                </span>
                <span className="text-3xl font-black text-white tracking-tight" data-id="element-1444">
                  Floor Coverings International
                </span>
                <span className="text-[10px] font-semibold text-white/90" data-id="element-1445">
                  Carpet &bull; Hardwood &bull; Laminate &bull; Tile &bull;
                  Vinyl &bull; Stone
                </span>
              </div>
              <p className="text-xs text-white/80 font-medium" data-id="element-1446">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-1447">
              <div className="flex flex-col gap-4" data-id="element-1448">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1449">
                  Home
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1450">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1451">
                  Terms Of Use
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-1452">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1453">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1454">
                  Do Not Sell My Personal Information
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8" data-id="element-1455">
            <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-1456">
              Floor Coverings International services are available nationwide.
              All estimates are free and no-obligation. Actual project costs may
              vary based on scope, materials, and local conditions. Licensed and
              insured. Contact us for full warranty details.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}